import React, { useEffect, useState } from 'react';
import { payrollAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { downloadBlob } from './_util';

// v4.37.1 item 1: Finance NEVER pastes JSON. Every table type has a guided
// form; bracketed types (federal/state income) are entered as editable rows
// (Add row / Remove row). Columns + a starter example row come from the same
// backend template definition used for the Excel download, so the form and the
// spreadsheet always agree.
const TYPES = [
  { v: 'federal_income', label: 'Federal income', multi: true, freq: true },
  { v: 'fica', label: 'FICA (Social Security & Medicare)', multi: false },
  { v: 'futa', label: 'FUTA', multi: false },
  { v: 'state_income', label: 'State income', multi: true },
  { v: 'suta', label: 'SUTA (state unemployment)', multi: true },
  { v: 'state_program', label: 'State program (e.g. DC PFL, Maryland FAMLI)', multi: true },
  { v: 'local_income', label: 'Local income', multi: true },
  { v: 'reciprocity', label: 'Reciprocity', multi: true },
  { v: 'ccpa', label: 'CCPA garnishment limits', multi: false },
];

const statusLabel = (t) => {
  if (t.status === 'withdrawn') return 'Withdrawn (seed, not for production)';
  if (t.status === 'approved' && t.seeded) return 'Approved (seed, Finance must verify)';
  if (t.status === 'approved') return 'Approved';
  if (t.status === 'draft') return 'Draft';
  if (t.status === 'superseded') return 'Superseded';
  return t.status;
};

// item 2/3: render a stored payload (cents/micros) in plain form — no internal
// field names, dollars and percents instead of raw integers.
// v4.37.3 item 4: explicit plain-English labels for known payload keys first,
// so the screen never shows internal words like "Hoh", "std_other" or "checked".
const KEY_LABELS = {
  std_other: 'Standard deduction (single and other)',
  std_single: 'Standard deduction (single and other)',
  std_mfj: 'Standard deduction (married filing jointly)',
  allowance_amount: 'Allowance amount',
  supplemental_rate: 'Supplemental flat rate',
  periods_per_year: 'Pay periods per year',
  brackets_2020: '2020-or-later W-4',
  brackets_pre2020: 'Before-2020 W-4',
  pre2020: 'Before-2020 W-4',
  standard: 'W-4 Step 2 not checked',
  checked: 'W-4 Step 2 checked',
  single: 'Single',
  married: 'Married filing jointly',
  mfj: 'Married filing jointly',
  mfs: 'Married filing separately',
  hoh: 'Head of household',
  ss_rate: 'Social Security rate',
  ss_wage_base: 'Social Security wage base',
  medicare_rate: 'Medicare rate',
  addl_medicare_threshold: 'Additional Medicare threshold',
  addl_medicare_rate: 'Additional Medicare rate',
  rate: 'Rate',
  wage_base: 'Wage base',
  flat_rate: 'Flat rate',
  standard_deduction: 'Standard deduction',
  employer_rate: 'Employer rate',
  employee_rate: 'Employee rate',
  credit_reduction_state: 'Credit-reduction state',
  credit_reduction: 'Credit-reduction rate',
  ccpa_general: 'CCPA general limit',
  ccpa_support: 'CCPA child-support limit',
};
// Plain labels for known scalar VALUES (e.g. a filing_status of "hoh").
const VALUE_LABELS = {
  hoh: 'Head of household', single: 'Single', married: 'Married filing jointly',
  mfj: 'Married filing jointly', mfs: 'Married filing separately',
  work: 'Work state', resident: 'Resident state',
};
const prettyKey = (k) => {
  const base = k.replace(/_micros$/, '').replace(/_cents$/, '');
  if (KEY_LABELS[base]) return KEY_LABELS[base];
  const words = base.replace(/\b(ss|mfj|mfs|hoh|ccpa|suta|futa|pfl|apm|pm)\b/gi, (m) => m.toUpperCase()).replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};
const fmtVal = (k, v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  if (k.endsWith('_micros')) return `${(Number(v) / 10000).toLocaleString(undefined, { maximumFractionDigits: 4 })}%`;
  if (k.endsWith('_cents')) return `$${(Number(v) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const s = String(v);
  return VALUE_LABELS[s.toLowerCase()] || s;
};
const BracketTable = ({ rows }) => (
  <table className="w-full text-xs border rounded my-1">
    <thead><tr className="text-left text-muted-foreground"><th className="p-1">Bracket: over ($)</th><th>Bracket: base tax ($)</th><th>Bracket: rate (%)</th></tr></thead>
    <tbody>{rows.map((b, i) => (
      <tr key={i} className="border-t"><td className="p-1">{fmtVal('over_cents', b.over_cents)}</td><td>{fmtVal('base_cents', b.base_cents)}</td><td>{fmtVal('rate_micros', b.rate_micros)}</td></tr>
    ))}</tbody>
  </table>
);
const isBracketArray = (v) => Array.isArray(v) && v.length > 0 && typeof v[0] === 'object' && 'rate_micros' in v[0];
// Lowercase render helper (NOT a PascalCase component) so react-refresh does
// not instrument the self-recursion and crash the babel traversal at build.
function renderPlainValue(k, value) {
  const label = prettyKey(k) || k;
  if (isBracketArray(value)) return (<div className="py-1" key={k}><p className="text-xs font-medium text-muted-foreground">{label}</p><BracketTable rows={value} /></div>);
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return (
      <div className="py-1 pl-3 border-l dark:border-slate-700" key={k}>
        <p className="text-xs font-medium text-muted-foreground">{label}</p>
        {Object.entries(value).map(([ck, v]) => renderPlainValue(ck, v))}
      </div>
    );
  }
  if (Array.isArray(value)) return (<p className="py-0.5 text-sm" key={k}><span className="text-muted-foreground">{label}:</span> {value.join(', ') || '—'}</p>);
  return (<p className="py-0.5 text-sm" key={k}><span className="text-muted-foreground">{label}:</span> <span className="font-medium text-foreground">{fmtVal(k, value)}</span></p>);
}

const blankRow = (columns) => Object.fromEntries(columns.map((c) => [c, '']));

export const TaxTablesSection = () => {
  const [tables, setTables] = useState([]);
  const [meta, setMeta] = useState({ table_type: 'state_income', tax_year: '2026', effective_date: '2026-01-01', pay_frequency: 'semi-monthly' });
  const [columns, setColumns] = useState([]);
  const [colLabels, setColLabels] = useState({});
  const [rows, setRows] = useState([]);
  const [cf, setCf] = useState({ table_type: 'state_income', jurisdiction: 'OK', from_year: '2025', to_year: '2026', effective_date: '2026-01-01' });
  const [viewDoc, setViewDoc] = useState(null);

  const current = TYPES.find((t) => t.v === meta.table_type) || TYPES[0];

  // v4.38.0 item 9: reflect the ACTIVE environment. In production seeded tables
  // are withdrawn (never drive a real run); in preview they are approved-seed.
  const seededWithdrawn = tables.some((t) => t.seeded && t.status === 'withdrawn');
  const seedLabel = seededWithdrawn
    ? '“Withdrawn (seed, not for production)”'
    : '“Approved (seed, Finance must verify)”';

  const load = () => payrollAPI.taxTables().then((r) => setTables(r.data.tables || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  // Load the column set + labels + example row whenever the table type changes.
  useEffect(() => {
    let active = true;
    payrollAPI.taxTemplateColumns(meta.table_type).then((r) => {
      if (!active) return;
      const cols = r.data.columns || [];
      const examples = r.data.examples || [];
      setColumns(cols);
      setColLabels(r.data.labels || {});
      setRows(examples.length ? examples.map((ex) => ({ ...blankRow(cols), ...ex })) : [blankRow(cols)]);
    }).catch(() => { setColumns([]); setColLabels({}); setRows([]); });
    return () => { active = false; };
  }, [meta.table_type]);

  const setCell = (i, key, val) => setRows(rows.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
  const addRow = () => setRows([...rows, blankRow(columns)]);
  const delRow = (i) => setRows(rows.filter((_, idx) => idx !== i));

  const createDraft = async () => {
    try {
      const body = {
        table_type: meta.table_type, tax_year: Number(meta.tax_year),
        effective_date: meta.effective_date, rows,
      };
      if (current.freq) body.pay_frequency = meta.pay_frequency;
      const r = await payrollAPI.taxTableUpload(body);
      if (!r.data.ok) {
        const first = (r.data.errors || [])[0];
        return toast.error(first ? `Row ${first.row}: ${first.error}` : 'Could not save — check the rows');
      }
      toast.success(`Draft table saved (${r.data.saved}) — a different Finance user must approve it`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create draft'); }
  };

  const approve = async (id) => {
    try { await payrollAPI.taxTableApprove(id); toast.success('Approved'); setViewDoc(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Approval blocked'); }
  };

  const openView = async (id) => {
    try { const r = await payrollAPI.taxTableDetail(id); setViewDoc(r.data); }
    catch (e) { toast.error('Could not open table'); }
  };

  const newVersion = async (id) => {
    try { await payrollAPI.taxTableNewVersion(id); toast.success('Draft created from this table — edit it, then a different Finance user approves'); setViewDoc(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not create a new version'); }
  };

  const typeLabel = (v) => (TYPES.find((t) => t.v === v) || {}).label || v;

  const getTemplate = async (tt) => {
    try { const r = await payrollAPI.taxTemplate(tt); downloadBlob(r, `tax_template_${tt}.xlsx`); }
    catch (e) { toast.error('No template'); }
  };

  const copyForward = async () => {
    try { await payrollAPI.taxTableCopyForward({ ...cf, from_year: Number(cf.from_year), to_year: Number(cf.to_year) }); toast.success('Copied forward as a Draft'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'No approved source table to copy'); }
  };

  return (
    <div className="space-y-6">
      <Card data-testid="netpay-tables">
        <CardHeader><CardTitle>Tax tables</CardTitle>
          <CardDescription>Effective-dated and versioned. Only Approved tables are used by the engine. Two-person control: the author can never approve their own table. Seeded starter tables are synthetic and labelled {seedLabel}; in production they are withdrawn so they never drive a real run — Finance must enter and approve verified tables.</CardDescription></CardHeader>
        <CardContent>
          <div className="overflow-auto max-h-80 border rounded text-sm">
            <table className="w-full">
              <thead><tr className="text-left text-xs text-muted-foreground">
                <th className="p-2">Type</th><th>Jurisdiction</th><th>Year</th><th>Effective</th><th>Version</th><th>Status</th><th>Action</th></tr></thead>
              <tbody>
                {tables.map((t) => (
                  <tr key={t.id} className="border-t" data-testid={`tax-table-${t.id}`}>
                    <td className="p-2">{typeLabel(t.table_type)}</td><td>{t.jurisdiction}</td><td>{t.tax_year}</td>
                    <td>{t.effective_date}</td><td>{t.version}</td>
                    <td><Badge variant={t.status === 'approved' ? 'default' : 'outline'} data-testid={`tax-table-status-${t.id}`}>{statusLabel(t)}</Badge></td>
                    <td className="flex gap-1 p-1">
                      <Button size="sm" variant="outline" data-testid={`tax-table-view-${t.id}`} onClick={() => openView(t.id)}>View</Button>
                      {t.status === 'draft' && <Button size="sm" variant="outline" data-testid={`tax-table-approve-${t.id}`} onClick={() => approve(t.id)}>Approve</Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!viewDoc} onOpenChange={(o) => { if (!o) setViewDoc(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-auto" data-testid="tax-table-view-dialog">
          {viewDoc && (
            <>
              <DialogHeader>
                <DialogTitle>{typeLabel(viewDoc.table_type)} — {viewDoc.jurisdiction} ({viewDoc.tax_year})</DialogTitle>
                <DialogDescription>Every value in plain form. {viewDoc.status === 'approved' ? 'Approved tables are read-only — use “New version from this table” to change them.' : 'Review the full table below before approving.'}</DialogDescription>
              </DialogHeader>
              <div className="space-y-1 text-sm" data-testid="tax-table-view-body">
                <div className="grid grid-cols-2 gap-2 border rounded p-3 bg-muted/50">
                  <p><span className="text-muted-foreground">Status:</span> <span className="font-medium">{statusLabel(viewDoc)}</span></p>
                  <p><span className="text-muted-foreground">Version:</span> <span className="font-medium">{viewDoc.version}</span></p>
                  <p><span className="text-muted-foreground">Effective date:</span> <span className="font-medium">{viewDoc.effective_date}</span></p>
                  {viewDoc.pay_frequency && <p><span className="text-muted-foreground">Pay frequency:</span> <span className="font-medium">{viewDoc.pay_frequency}</span></p>}
                  <p><span className="text-muted-foreground">Entered by:</span> <span className="font-medium">{viewDoc.created_by || '—'}</span></p>
                  <p><span className="text-muted-foreground">Approved by:</span> <span className="font-medium">{viewDoc.approved_by || '—'}</span></p>
                  {viewDoc.note && <p className="col-span-2"><span className="text-muted-foreground">Note:</span> <span className="font-medium">{viewDoc.note}</span></p>}
                </div>
                <p className="pt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Rates, wage bases and brackets</p>
                <div className="border rounded p-3">
                  {Object.entries(viewDoc.payload || {}).map(([k, v]) => renderPlainValue(k, v))}
                </div>
              </div>
              <DialogFooter>
                {viewDoc.status === 'draft' && (
                  <Button data-testid="tax-view-approve" onClick={() => approve(viewDoc.id)}>Approve this table</Button>
                )}
                {viewDoc.status === 'approved' && (
                  <Button variant="outline" data-testid="tax-view-new-version" onClick={() => newVersion(viewDoc.id)}>New version from this table</Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Card data-testid="netpay-table-new">
        <CardHeader><CardTitle>New draft table</CardTitle>
          <CardDescription>Pick a table type and fill the guided form — plain dollar and percent fields, with bracket tables entered as editable rows. You never paste JSON. Prefer a spreadsheet? Download the Excel template for that exact type, fill it and upload it back.</CardDescription></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <label className="text-sm block">Table type
              <select data-testid="draft-type" value={meta.table_type} onChange={(e) => setMeta({ ...meta, table_type: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                {TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
              </select>
            </label>
            <label className="text-sm">Tax year<Input data-testid="draft-year" value={meta.tax_year} onChange={(e) => setMeta({ ...meta, tax_year: e.target.value })} /></label>
            <label className="text-sm">Effective date<Input data-testid="draft-eff" value={meta.effective_date} onChange={(e) => setMeta({ ...meta, effective_date: e.target.value })} /></label>
            {current.freq && (
              <label className="text-sm">Pay frequency<Input data-testid="draft-freq" value={meta.pay_frequency} onChange={(e) => setMeta({ ...meta, pay_frequency: e.target.value })} /></label>
            )}
          </div>

          <div className="overflow-x-auto border rounded">
            <table className="w-full text-sm" data-testid="draft-rows">
              <thead><tr className="text-left text-xs text-muted-foreground">
                {columns.map((c) => <th key={c} className="p-2 whitespace-nowrap">{colLabels[c] || c}</th>)}
                <th className="p-2" />
              </tr></thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} className="border-t" data-testid={`draft-row-${i}`}>
                    {columns.map((c) => (
                      <td key={c} className="p-1">
                        <Input className="h-8 min-w-28" data-testid={`draft-cell-${i}-${c}`} value={row[c] ?? ''}
                          onChange={(e) => setCell(i, c, e.target.value)} />
                      </td>
                    ))}
                    <td className="p-1">
                      {current.multi && rows.length > 1 && (
                        <Button type="button" size="sm" variant="ghost" data-testid={`draft-row-del-${i}`} onClick={() => delRow(i)}>Remove</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap gap-2">
            {current.multi && <Button variant="outline" size="sm" data-testid="draft-row-add" onClick={addRow}>Add row</Button>}
            <Button data-testid="draft-create" onClick={createDraft}>Create draft</Button>
            <Button variant="outline" data-testid="draft-template" onClick={() => getTemplate(meta.table_type)}>Download Excel template</Button>
          </div>
          <p className="text-xs text-muted-foreground">Percent columns are percents (4.75 means 4.75%). Dollar columns are whole/decimal dollars. Everything saves as a Draft for a second Finance user to approve.</p>
        </CardContent>
      </Card>

      <Card data-testid="netpay-copy-forward">
        <CardHeader><CardTitle>Copy forward</CardTitle>
          <CardDescription>Copy last year's approved table into a new year as a Draft starting point (YTD/new-year roll).</CardDescription></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
            <label className="text-sm block">Type
              <select data-testid="cf-type" value={cf.table_type} onChange={(e) => setCf({ ...cf, table_type: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                {TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
              </select>
            </label>
            <label className="text-sm">Jurisdiction<Input data-testid="cf-juris" value={cf.jurisdiction} onChange={(e) => setCf({ ...cf, jurisdiction: e.target.value })} /></label>
            <label className="text-sm">From year<Input data-testid="cf-from" value={cf.from_year} onChange={(e) => setCf({ ...cf, from_year: e.target.value })} /></label>
            <label className="text-sm">To year<Input data-testid="cf-to" value={cf.to_year} onChange={(e) => setCf({ ...cf, to_year: e.target.value })} /></label>
            <Button data-testid="cf-run" onClick={copyForward}>Copy forward</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
